import { supabase } from "@/integrations/supabase/client";

const FIRST_TOUCH_KEY = "first_touch_attribution";
const LS_TTL_MS = 30 * 60 * 1000; // 30 min for Safari ITP fallback

export interface FirstTouchAttribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  landing_path: string | null;
  referrer: string | null;
  captured_at: string;
}

export function parseUtmFromSearch(search: string) {
  const params = new URLSearchParams(search);
  return {
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_term: params.get("utm_term"),
    utm_content: params.get("utm_content"),
  };
}

/** Capture first-touch attribution. First write wins; never overwrites. */
export function captureFirstTouchIfAbsent(pathname: string, search: string): FirstTouchAttribution | null {
  try {
    const existing = readFirstTouch();
    if (existing) return existing;

    const utms = parseUtmFromSearch(search);
    const hasAny = Object.values(utms).some(Boolean);
    // Always record landing_path/referrer on first view, even if no UTMs.
    const record: FirstTouchAttribution = {
      ...utms,
      landing_path: pathname || null,
      referrer: document.referrer || null,
      captured_at: new Date().toISOString(),
    };
    const json = JSON.stringify(record);
    try {
      sessionStorage.setItem(FIRST_TOUCH_KEY, json);
    } catch {
      // ignore
    }
    // localStorage fallback for OAuth redirect flows + Safari ITP edge cases
    try {
      localStorage.setItem(FIRST_TOUCH_KEY, json);
    } catch {
      // ignore
    }
    void hasAny;
    return record;
  } catch {
    return null;
  }
}

export function readFirstTouch(): FirstTouchAttribution | null {
  try {
    const raw = sessionStorage.getItem(FIRST_TOUCH_KEY);
    if (raw) return JSON.parse(raw) as FirstTouchAttribution;
  } catch {
    // ignore
  }
  // localStorage fallback with TTL (OAuth round-trip)
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FirstTouchAttribution;
    const captured = Date.parse(parsed.captured_at);
    if (Number.isFinite(captured) && Date.now() - captured <= LS_TTL_MS) {
      // re-hydrate sessionStorage so subsequent reads are cheap
      try { sessionStorage.setItem(FIRST_TOUCH_KEY, raw); } catch { /* ignore */ }
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

/** Persist signup attribution row. Idempotent via PK conflict. */
export async function persistSignupAttribution(userId: string) {
  if (!userId) return;
  const ft = readFirstTouch();
  const row = {
    user_id: userId,
    utm_source: ft?.utm_source ?? null,
    utm_medium: ft?.utm_medium ?? null,
    utm_campaign: ft?.utm_campaign ?? null,
    utm_term: ft?.utm_term ?? null,
    utm_content: ft?.utm_content ?? null,
    referrer: ft?.referrer ?? (typeof document !== "undefined" ? document.referrer || null : null),
    landing_path: ft?.landing_path ?? (typeof window !== "undefined" ? window.location.pathname : null),
  };
  try {
    const { error } = await supabase
      .from("signup_attribution")
      .upsert(row, { onConflict: "user_id", ignoreDuplicates: true });
    if (error) console.warn("[attribution] insert failed", error.message);
  } catch (err) {
    console.warn("[attribution] insert threw", err);
  }
}
